import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateTime, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { TaskTypeBadge, TransferTaskActions } from "@/components/TaskTypeBadge";

const PRIORITY_LABEL = { niedrig: "Niedrig", normal: "Normal", hoch: "Hoch", dringend: "Dringend" };
const PRIORITY_CLASS = { niedrig: "bg-slate-100 text-slate-700", normal: "bg-blue-50 text-blue-700", hoch: "bg-amber-50 text-amber-800", dringend: "bg-rose-50 text-rose-700" };
const TSTATUS_LABEL = { offen: "Offen", in_bearbeitung: "In Bearbeitung", blockiert: "Blockiert", erledigt: "Erledigt", abgebrochen: "Abgebrochen" };

const DEADLINE_FILTERS = [
  { k: "", l: "Alle" },
  { k: "overdue", l: "Überfällig" },
  { k: "today", l: "Heute" },
  { k: "week", l: "Diese Woche" },
];

export default function Aufgaben() {
  const nav = useNavigate();
  const me = getUser();
  const [tab, setTab] = useState("mine");
  const [tasks, setTasks] = useState([]);
  const [statusF, setStatusF] = useState("");
  const [priorityF, setPriorityF] = useState("");
  const [deadlineF, setDeadlineF] = useState("");

  const load = async () => {
    const params = { mine: tab === "mine" };
    if (statusF) params.status = statusF;
    if (priorityF) params.priority = priorityF;
    if (deadlineF) params.deadline_filter = deadlineF;
    try { const r = await api.get("/tasks", { params }); setTasks(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Aufgaben konnten nicht geladen werden."); }
  };
  useEffect(() => { load();  }, [tab, statusF, priorityF, deadlineF]);

  const changeStatus = async (t, status) => {
    try { await api.patch(`/tasks/${t.id}`, { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Statuswechsel nicht möglich."); }
  };

  const canSeeAll = me.rolle === "admin" || me.rolle === "manager";

  return (
    <div className="space-y-6" data-testid="aufgaben-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Aufgaben</h1>
        <p className="text-sm text-slate-500 mt-1">Ihre Aufgaben und Aufgaben des Teams.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-mine">Meine Aufgaben</TabsTrigger>
          {canSeeAll && <TabsTrigger value="all" data-testid="tab-all">Alle Aufgaben</TabsTrigger>}
        </TabsList>
        <TabsContent value={tab} className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-3">
            <Select value={statusF || "__all__"} onValueChange={(v) => setStatusF(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="task-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Alle Status</SelectItem>{Object.entries(TSTATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={priorityF || "__all__"} onValueChange={(v) => setPriorityF(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="task-filter-priority"><SelectValue placeholder="Priorität" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Alle Prioritäten</SelectItem>{Object.entries(PRIORITY_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-1">
              {DEADLINE_FILTERS.map((f) => (
                <button key={f.k || "alle"} onClick={() => setDeadlineF(f.k)} data-testid={`task-filter-deadline-${f.k || "alle"}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border ${deadlineF === f.k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                  {f.l}
                </button>
              ))}
            </div>
          </div>
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="aufgaben-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Nr.</th>
                    <th className="px-4 py-3">Titel</th>
                    <th className="px-4 py-3">Zugewiesen</th>
                    <th className="px-4 py-3">Priorität</th>
                    <th className="px-4 py-3">Deadline</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Auftrag</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Keine Aufgaben.</td></tr>}
                  {tasks.map((t) => (
                    <tr key={t.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`task-row-${t.task_nummer}`}>
                      <td className="px-4 py-3 font-mono text-xs">{t.task_nummer}</td>
                      <td className="px-4 py-3">
                        <div className="mb-1"><TaskTypeBadge typ={t.typ} testid={`task-typ-${t.task_nummer}`} /></div>
                        <div className="font-medium text-slate-900">{t.titel}</div>
                        {t.beschreibung && <div className="text-xs text-slate-500 line-clamp-1">{t.beschreibung}</div>}
                        <TransferTaskActions task={t} onDone={load} />
                      </td>
                      <td className="px-4 py-3 text-slate-700">{t.assignee_name || "—"}</td>
                      <td className="px-4 py-3"><Badge className={PRIORITY_CLASS[t.priority] + " border-0"}>{PRIORITY_LABEL[t.priority]}</Badge></td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{t.deadline ? fmtDateTime(t.deadline) : "—"}</td>
                      <td className="px-4 py-3">
                        <Select value={t.status} onValueChange={(v) => changeStatus(t, v)}>
                          <SelectTrigger className="h-8 w-44" data-testid={`task-status-${t.task_nummer}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.entries(TSTATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        {t.related_order_id && <Button size="sm" variant="outline" onClick={() => nav(`/auftraege/${t.related_order_id}`)} data-testid={`task-open-order-${t.task_nummer}`}><ExternalLink className="w-3.5 h-3.5 mr-1" /> Öffnen</Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
